import {
  IAwardBiddingPayload,
  IBidPackageResponse,
  IBidPackageResponseListItem,
  IBidResponse,
  IDisplayBidNFA,
  IDisplayBidPairing,
  IWebsocketBidPkgBuildProgress,
} from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { ThunkDispatch } from "redux-thunk";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { ICrewEvent, ICrewNonFlyingAssignmentMap } from "../reducers/CrewScheduleReducer";

export enum Type {
  GOT_BID_PACKAGE_LIST = "GOT_BID_PACKAGE_LIST",
  SET_BID_PACKAGE = "SET_BID_PACKAGE",
  UPDATE_CREW_BID_EVENT = "UPDATE_CREW_BID_EVENT",
  TOGGLE_ASSIGN_ALL_CREW_BIDS_RANK = "TOGGLE_ASSIGN_ALL_CREW_BIDS_RANK",
  SET_BID_NFA_COUNTS = "SET_BID_NFA_COUNTS",
  GOT_CREWMEMBER_BIDS = "GOT_CREWMEMBER_BIDS",
  RESET_BIDDING_TOOL = "RESET_BIDDING_TOOL",
  ADD_BID_PAIRING_CREATION_PROGRESS = "ADD_BID_PAIRING_CREATION_PROGRESS",
  SET_AWARDED_BIDS_VISIBILITY = "SET_AWARDED_BIDS_VISIBILITY",
}

export interface IBiddingNFACounts {
  [date: string]: {
    // nfaId was being used here but was changed to title as the crew schedule data (awarded) don't contain _ids.
    [nfaTitle: string]: {
      // object containing nfa type ids with their current assigned count and max count (max count is determined by crew count on package for the moment)
      maxCount: number;
      assignedAwarded: string[]; // Stores array of all assigned/awarded nfa assignments (used to ensure count is accurate)
      count: number;
      color: string;
      title: string;
      description: string;
    };
  };
}

export interface IGotBidPackageList {
  type: Type.GOT_BID_PACKAGE_LIST;
  bidPackages: IBidPackageResponseListItem[];
}

export interface ISetBidPackage {
  type: Type.SET_BID_PACKAGE;
  bidPackage: IBidPackageResponse;
}

export interface IUpdateCrewBidPairing {
  type: Type.UPDATE_CREW_BID_EVENT;
  crewId: string;
  bidPairing?: IDisplayBidPairing;
  bidNFA?: IDisplayBidNFA;
}

export interface IToggleAssignCrewBidRank {
  type: Type.TOGGLE_ASSIGN_ALL_CREW_BIDS_RANK;
  crewId: string;
  rank: number;
  assign?: boolean;
}

export interface ISetBidNFACounts {
  type: Type.SET_BID_NFA_COUNTS;
  biddingNFACounts: IBiddingNFACounts;
}

export interface IGotCrewMemberbids {
  type: Type.GOT_CREWMEMBER_BIDS;
  crewMemberBids: IBidResponse;
}

export interface IResetBiddingTool {
  type: Type.RESET_BIDDING_TOOL;
}

export interface IAddBidPairingCreationProgress {
  type: Type.ADD_BID_PAIRING_CREATION_PROGRESS;
  progress: IWebsocketBidPkgBuildProgress;
}

export interface ISetAwardedBidsVisibility {
  type: Type.SET_AWARDED_BIDS_VISIBILITY;
  isVisible: boolean;
}

export type Action =
  | IGotBidPackageList
  | ISetBidPackage
  | IUpdateCrewBidPairing
  | IToggleAssignCrewBidRank
  | ISetBidNFACounts
  | IGotCrewMemberbids
  | IResetBiddingTool
  | IAddBidPairingCreationProgress
  | ISetAwardedBidsVisibility;

export const getBidPackageList = async (): Promise<IBidPackageResponseListItem[]> => {
  const response: IBidPackageResponseListItem[] = await AmplifyAPIWrapper.Get("/bidding/packages", null);
  return response;
};

export const gotBidPackageList = (bidPackages: IBidPackageResponseListItem[]): IGotBidPackageList => {
  return {
    type: Type.GOT_BID_PACKAGE_LIST,
    bidPackages,
  };
};

export const setBidPackage = (bidPackage: IBidPackageResponse): ISetBidPackage => {
  return {
    type: Type.SET_BID_PACKAGE,
    bidPackage,
  };
};

export const setBidNFACounts = (biddingNFACounts: IBiddingNFACounts): ISetBidNFACounts => {
  return {
    type: Type.SET_BID_NFA_COUNTS,
    biddingNFACounts,
  };
};

export const resetBiddingTool = (): IResetBiddingTool => {
  return {
    type: Type.RESET_BIDDING_TOOL,
  };
};

export const setAwardedBidsVisibility = (isVisible: boolean): ISetAwardedBidsVisibility => {
  return {
    type: Type.SET_AWARDED_BIDS_VISIBILITY,
    isVisible,
  };
};

/**
 *  Websockets continually update the bid pairing creation status stored in an object with the key being the packageId
 * @param progress - the progress object containing package id as well as the progress of bid pairing creation.
 * @returns
 */
export const updatePackageBidPairingCreationProgress = (
  progress: IWebsocketBidPkgBuildProgress
): IAddBidPairingCreationProgress => {
  return {
    type: Type.ADD_BID_PAIRING_CREATION_PROGRESS,
    progress,
  };
};

/**
 * Placeholder function for loading in a particular crew members bids.
 * @param crewId - crew id
 * @param packageId - package id containing the crew and their bids.
 */
export const getCrewBids = async (
  crewId: string,
  pkgId: string,
  dispatch?: ThunkDispatch<object, object, any>
): Promise<IBidResponse> => {
  const notifications: INotifications = dispatch
    ? {
        dispatch: dispatch,
        infoMessage: "Loading Crew Bids",
        successMessage: "Crew Bids Loaded Successfully",
        errorMessage: "Error: Unable to Load Crew Bids",
      }
    : null;
  const response: IBidResponse = await AmplifyAPIWrapper.Get(`/bidding/award/${pkgId}/${crewId}`, notifications);
  return response;
};

/**
 * Award/Unward crew bids for selected crew member.
 * @param payload -
 * @param dispatch -
 * @returns
 */
export const updateCrewBids = async (
  payload: IAwardBiddingPayload,
  dispatch?: ThunkDispatch<object, object, any>,
  toggleDone?: boolean // if true, change notifications as only done status is being modified.
): Promise<IBidResponse> => {
  const notifications: INotifications = dispatch
    ? {
        dispatch: dispatch,
        infoMessage: toggleDone
          ? `Setting Bid Assignment Status: ${payload.isDone ? "Complete" : "Incomplete"} for crew member`
          : "Updating Crew Bids",
        successMessage: toggleDone ? "Bid Assignment Status Set Successfully" : "Crew Bids Updated Successfully",
        errorMessage: toggleDone ? "Error: Unable to Set Bid Assignment Status" : "Error: Unable to Update Crew Bids",
      }
    : null;
  const response: IBidResponse = await AmplifyAPIWrapper.Post("/bidding/award", payload, notifications);
  return response;
};

export const gotCrewMemberBids = (crewMemberBids: IBidResponse): IGotCrewMemberbids => {
  return {
    type: Type.GOT_CREWMEMBER_BIDS,
    crewMemberBids,
  };
};

/**
 * Function used to update a given bid pairing or nfa on a selected crew members loaded crew bids.
 * @param crewEvent -
 * @returns -
 */
export const updateCrewBidEvent = (crewEvent: ICrewEvent, type: "pairing" | "nfa"): IUpdateCrewBidPairing => {
  return {
    type: Type.UPDATE_CREW_BID_EVENT,
    crewId: crewEvent.crewId,
    bidPairing: type === "pairing" ? (crewEvent.event as IDisplayBidPairing) : null,
    bidNFA: type === "nfa" ? (crewEvent.event as IDisplayBidNFA) : null,
  };
};

/**
 * Function used to assign/unassign all bid pairings in a given rank.
 */
export const toggleAssignmentCrewBidRank = (
  crewId: string,
  rank: number,
  assign?: boolean
): IToggleAssignCrewBidRank => {
  return {
    type: Type.TOGGLE_ASSIGN_ALL_CREW_BIDS_RANK,
    crewId,
    rank,
    assign,
  };
};

/**
 * Function to create bidding NFA counts that will show below the standard time axis.
 * @param bidPackage - bidding package
 * return - IBiddingNFACounts
 */
export const createBiddingNFACounts = (
  bidPackage: IBidPackageResponse,
  crewNFAs: ICrewNonFlyingAssignmentMap
): IBiddingNFACounts => {
  if (
    !bidPackage?.bidPackage?.bidBlockStart ||
    !bidPackage?.bidPackage?.bidBlockEnd ||
    !bidPackage?.bidPackage?.nonFlyingAssignments?.length ||
    !bidPackage?.crewBids?.length
  ) {
    return null;
  }
  const dateOne = moment(bidPackage.bidPackage.bidBlockStart);
  const dateTwo = moment(bidPackage.bidPackage.bidBlockEnd);
  const nfaTypes = bidPackage.bidPackage.nonFlyingAssignments;
  const biddingDays: { [key: string]: any } = {};

  // Create initial bidding days object for all types (set counts to zero initially)
  for (let i = dateOne; i < dateTwo; i.add(1, "day")) {
    const dateKey: string = i.utc().format("YYYY/MM/DD");
    const dateInfo: { [key: string]: any } = {};
    nfaTypes?.forEach((nfaTypes) => {
      // use title for key as type id isn't being returned in the crew NFAs in schedule.
      dateInfo[nfaTypes?.nfaType?.title] = {
        maxCount: bidPackage?.crewBids?.length, // max count allowed for assignment of this nfa type.
        assignedAwarded: [],
        count: 0, // current count assigned of this type of nfa type.
        color: nfaTypes?.nfaType?.defaultColor ?? "cccccc", // assigned NFA color.
        title: nfaTypes?.nfaType?.title,
        description: nfaTypes?.nfaType?.description,
      };
    });
    // Set info in biddingDays
    biddingDays[dateKey] = dateInfo;
  }

  // Array of crew ids in package.
  const crewIdsInPackage: string[] = (bidPackage?.crewBids?.map((result) => result.userId) as any[]) ?? [];

  // Iterate through all actively awarded NFAs in data and increment type counts if applicable.
  Object.entries(crewNFAs)?.forEach((nfaAssignment) => {
    const [crewId, nfaObject] = nfaAssignment;
    // Applies only to NFA assigned to crew in package.
    if (crewIdsInPackage?.includes(crewId)) {
      nfaObject?.nonFlyingAssignments?.forEach((awardedItem) => {
        const NFATypeTitle = awardedItem?.assignmentType?.title;
        const NFAStartDate = moment(awardedItem?.start)?.utc().format("YYYY/MM/DD");
        if (biddingDays?.[NFAStartDate]?.[NFATypeTitle]) {
          // console.log("increment count for this NFA type");
          const updatedAssignmentArray = [...biddingDays[NFAStartDate][NFATypeTitle].assignedAwarded];
          updatedAssignmentArray.push(awardedItem?._id);
          biddingDays[NFAStartDate][NFATypeTitle].assignedAwarded = updatedAssignmentArray;
          biddingDays[NFAStartDate][NFATypeTitle].count = updatedAssignmentArray?.length ?? 0;
        }
      });
    }
    return true;
  });

  return biddingDays;
};
